import type React from 'react';
import { useEffect, useMemo, useState } from 'react';

import { ErrorPage } from '../components/ErrorInterrupter';
import { LoadingFallback } from '../components/progress/LoadingFallback';
import { useAsyncState } from '../model/state/asyncState';
import StudyService from '../service/StudyService';

import { Head } from './library/parts/Head';
import { SortItem } from './library/parts/SortItem';

enum ResourcesSortOrder {
  nameForward,
  nameReverse,
}

export const ExternalResourcesList: React.FC = () => {
  const state = useAsyncState(StudyService.getUserLibraryResources);

  const [sort, setSort] = useState<ResourcesSortOrder>(ResourcesSortOrder.nameForward);
  const sortedResources = useMemo(() => {
    if (!state.data) return [];
    const mutable = [...state.data];

    return mutable.sort((a, b) => {
      const resourceAName = a.title.toLowerCase();
      const resourceBName = b.title.toLowerCase();
      switch (sort) {
        case ResourcesSortOrder.nameForward:
          return resourceAName > resourceBName ? 1 : -1;
        case ResourcesSortOrder.nameReverse:
          return resourceAName > resourceBName ? -1 : 1;
        default:
          return 0;
      }
    });
  }, [sort, state]);

  useEffect(() => {
    document.title = 'Library – Blank Slate';
  }, []);

  if (state.error) return <ErrorPage error={state.error} />;

  return (
    <>
      {state.inProgress ? (
        <LoadingFallback />
      ) : (
        <div className="flex h-[88vh] w-full flex-col justify-between pl-[44px] pr-[48px] font-Lato text-[14px]">
          <Head text="Click on any item to review content" title="Extra Resources" />
          {sortedResources.length === 0 ? null : (
            <div className="mt-[18px] flex-1 rounded-[12px] bg-white px-[28px] pb-[33px] pt-[29px] shadow-[0_4px_4px_rgba(0,0,0,0.25)]">
              <div className="flex flex-row justify-between rounded-[12px] bg-[#E0E0E066] px-[21px] py-[9px]">
                <SortItem
                  disabled={state.inProgress}
                  name="Extra Resources"
                  onClick={() => {
                    if (sort === ResourcesSortOrder.nameForward) {
                      setSort(ResourcesSortOrder.nameReverse);
                    } else {
                      setSort(ResourcesSortOrder.nameForward);
                    }
                  }}
                />
              </div>
              <div className="overflow-y-auto px-[2px] pr-[12px]">
                {sortedResources.map((resource) => {
                  return (
                    <a href={resource.url} key={resource.title} rel="noopener noreferrer" target="_blank">
                      <div className="flex flex-row items-center justify-between border-b-[2px] border-[#0000001A] px-[21px] pb-[2px] pt-[3px] hover:border-b hover:border-[#0000001A] hover:pb-[3px]">
                        <div className="m-0 text-[#2D3540]">
                          <p className="my-[10px]">{resource.title}</p>
                        </div>
                      </div>
                    </a>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};
